import React, { useEffect, useRef, useState } from 'react'
import Menu from 'components/menu'
import { MenuItem, MenuLabel, MenuSeparator } from 'components/menu/items'
import { Tick } from 'components/icons'

interface SidebarMenuItemBase {
  testId: string
  /** Optional for one of a set of choices, whose tick shows at the row's end. */
  icon?: React.ReactNode
  label: string
  /** For product tours that point at this entry. */
  dataTour?: string
  /** Red, for an action that deletes. */
  variant?: 'default' | 'destructive'
  /** Why the viewer can't choose it: it shows greyed out, with this as its tooltip. */
  disabledReason?: string
  /** One of a set of choices: true for the current one, false for the others (menuitemradio). */
  checked?: boolean
  /** The pointer or focus came onto the entry (true) or left it (false), e.g. to preview what it acts on. */
  onHighlight?: (highlighted: boolean) => void
}

/** An action, or a row that opens a submenu of more entries (with a › at its end). */
export type SidebarMenuItem = SidebarMenuItemBase &
  ({ onSelect: () => void; submenu?: undefined } | { submenu: SidebarMenuEntry[]; onSelect?: undefined })

/** A hairline between groups of entries. */
export interface SidebarMenuSeparator {
  separator: true
}

/** A muted heading over the group of entries below it ("Organise"). */
export interface SidebarMenuHeading {
  heading: string
}

export type SidebarMenuEntry = SidebarMenuItem | SidebarMenuSeparator | SidebarMenuHeading

export const MENU_SEPARATOR: SidebarMenuSeparator = { separator: true }

const isSeparator = (entry: SidebarMenuEntry): entry is SidebarMenuSeparator => 'separator' in entry
const isHeading = (entry: SidebarMenuEntry): entry is SidebarMenuHeading => 'heading' in entry
const isItem = (entry: SidebarMenuEntry): entry is SidebarMenuItem => !isSeparator(entry) && !isHeading(entry)

/**
 * Drops the separators that would have nothing on one side of them, so a list built from optional
 * groups never starts, ends or doubles up on a line, and the headings with no entries under them.
 */
const tidySeparators = (entries: SidebarMenuEntry[]): SidebarMenuEntry[] =>
  entries.filter((entry, i) => {
    if (isHeading(entry)) return entries[i + 1] !== undefined && isItem(entries[i + 1])
    if (!isSeparator(entry)) return true
    const previous = entries[i - 1]
    return previous !== undefined && !isSeparator(previous) && entries.slice(i + 1).some(isItem)
  })

/** The submenu open under a row: which row, its element (focus goes back there) and where it opens. */
interface OpenSubmenu {
  testId: string
  anchor: HTMLElement
  top: number
  left: number
}

interface MenuEntryProps {
  item: SidebarMenuItem
  /** The row's submenu is open. */
  expanded: boolean
  onOpenSubmenu: (anchor: HTMLElement) => void
}

/**
 * One menu entry, a shared menu row (`components/menu/items`): a click, or Enter or Space once the
 * arrow keys have focused it. The keys click the entry rather than choosing it directly, so a product
 * tour listening for the click sees them. A row with a submenu opens it the same ways, and with →. A
 * disabled row stays in the arrow keys' reach, so its reason can be read.
 */
const MenuEntry: React.FC<MenuEntryProps> = ({ item, expanded, onOpenSubmenu }) => (
  <MenuItem
    role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
    aria-checked={item.checked}
    tabIndex={-1}
    keysClick
    disabledReason={item.disabledReason}
    submenu={item.submenu !== undefined}
    aria-haspopup={item.submenu ? 'menu' : undefined}
    aria-expanded={item.submenu ? expanded : undefined}
    icon={item.icon}
    variant={item.variant}
    onClick={(event) => {
      if (item.submenu) onOpenSubmenu(event.currentTarget)
      else item.onSelect()
    }}
    data-testid={item.testId}
    data-tour={item.dataTour}
  >
    {item.checked === undefined ? (
      item.label
    ) : (
      // One of a set: the current choice is ticked at the row's end, as in the app's other menus.
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.checked && <Tick className="shrink-0 size-4 text-body" aria-hidden />}
      </span>
    )}
  </MenuItem>
)

/** A menu entry, plain or one of a set. */
const ITEM_ROLES = ['menuitem', 'menuitemradio']
/** A menu's own entries, but not those of a submenu open beside it. */
const OWN_ITEMS = ITEM_ROLES.map((role) => `:scope > [role="${role}"]`).join(', ')
const ANY_ITEM = ITEM_ROLES.map((role) => `[role="${role}"]`).join(', ')

/** Up and Down step between the menu's own items, wrapping round; separators aren't items. */
const stepFocus = (menu: HTMLElement, step: 1 | -1): void => {
  const items = [...menu.querySelectorAll<HTMLElement>(OWN_ITEMS)]
  const at = items.findIndex((item) => item === document.activeElement)
  items[(at + step + items.length) % items.length]?.focus()
}

const SUBMENU_WIDTH = 200

interface SidebarMenuListProps {
  /** What the menu is for, for screen readers. */
  label: string
  /** A caption above the entries, such as how many rows the menu acts on. */
  heading?: string
  items: SidebarMenuEntry[]
  /** Tab leaves the menu, which closes it. Escape is the surrounding Menu's to handle. */
  onClose: () => void
  /**
   * For a submenu: ← or Escape closes just it, back to the row that opened it. Its keys go no
   * further, so the menu it opened from doesn't act on them too.
   */
  onBack?: () => void
}

/**
 * A sidebar menu's entries, worked from the keyboard as well as the pointer: focus starts on the
 * first entry, Up and Down move between them (skipping separators), Enter or Space chooses, and Tab
 * closes the menu. A row with a submenu opens it beside itself on a click, Enter, Space or →; ← or
 * Escape closes it again. Goes inside a `Menu`, which closes on Escape and on a click outside.
 */
const SidebarMenuList: React.FC<SidebarMenuListProps> = ({ label, heading, items, onClose, onBack }) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [submenu, setSubmenu] = useState<OpenSubmenu | null>(null)
  const entries = tidySeparators(items)
  const openItem = submenu ? entries.filter(isItem).find((e) => e.testId === submenu.testId) : undefined

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(ANY_ITEM)?.focus()
  }, [])

  // The entry the pointer or focus is on, for its onHighlight. Let go when the list goes.
  const highlightedRef = useRef<SidebarMenuItem | null>(null)
  const highlight = (item: SidebarMenuItem | null): void => {
    if (item === highlightedRef.current) return
    highlightedRef.current?.onHighlight?.(false)
    item?.onHighlight?.(true)
    highlightedRef.current = item
  }
  const itemAt = (target: EventTarget): SidebarMenuItem | null => {
    const row = target instanceof Element ? target.closest<HTMLElement>(ANY_ITEM) : null
    if (!row || row.parentElement !== listRef.current) return null
    return entries.filter(isItem).find((e) => e.testId === row.dataset.testid) ?? null
  }
  useEffect(() => () => highlightedRef.current?.onHighlight?.(false), [])

  const openSubmenu = (testId: string, anchor: HTMLElement): void => {
    const rect = anchor.getBoundingClientRect()
    // Level with the row, just past the menu's right edge.
    setSubmenu({ testId, anchor, top: rect.top - 4, left: rect.right + 8 })
  }

  const closeSubmenu = (): void => {
    submenu?.anchor.focus()
    setSubmenu(null)
  }

  return (
    <div
      ref={listRef}
      role="menu"
      tabIndex={-1}
      aria-label={label}
      className="flex flex-col p-1 outline-none"
      onPointerOver={(event) => highlight(itemAt(event.target))}
      onPointerLeave={() => highlight(null)}
      onFocus={(event) => highlight(itemAt(event.target))}
      onKeyDown={(event) => {
        // Keys from an open submenu bubble here through its portal; it has dealt with them.
        if (!event.currentTarget.contains(event.target as Node)) return
        if (onBack) event.stopPropagation()
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault()
          stepFocus(event.currentTarget, event.key === 'ArrowDown' ? 1 : -1)
        } else if (onBack && (event.key === 'ArrowLeft' || event.key === 'Escape')) {
          event.preventDefault()
          onBack()
        } else if (event.key === 'Escape') {
          // The Menu closes itself on Escape; this keeps the sidebar selection from also clearing.
          event.preventDefault()
        } else if (event.key === 'Tab') {
          event.preventDefault()
          onClose()
        }
      }}
    >
      {heading && (
        <div data-testid="sidebar-menu-heading">
          <MenuLabel>{heading}</MenuLabel>
        </div>
      )}
      {entries.map((entry, i) => {
        if (isSeparator(entry)) return <MenuSeparator key={`separator-${i}`} />
        if (isHeading(entry)) return <MenuLabel key={`heading-${entry.heading}`}>{entry.heading}</MenuLabel>
        return (
          <MenuEntry
            key={entry.testId}
            item={entry}
            expanded={submenu?.testId === entry.testId}
            onOpenSubmenu={(anchor) => openSubmenu(entry.testId, anchor)}
          />
        )
      })}
      {submenu && openItem?.submenu && (
        <Menu
          id={`${openItem.testId}-submenu`}
          menuState={{ open: true, top: `${submenu.top}px`, left: `${submenu.left}px`, right: 'auto', bottom: 'auto' }}
          setMenuState={(state) => {
            if (!state.open) setSubmenu(null)
          }}
          width={SUBMENU_WIDTH}
          fixed
          nested
        >
          <SidebarMenuList label={openItem.label} items={openItem.submenu} onClose={onClose} onBack={closeSubmenu} />
        </Menu>
      )}
    </div>
  )
}

export default SidebarMenuList
